import React, { useState } from 'react';
import { Mail, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { addEmailSubscriber } from '../../../../services/firestoreService';
import useBlockStyles from '../common/useBlockStyles';

/**
 * EmailSubscribeRenderer - Renders the email subscribe block
 * Standalone email capture form with rate limiting and themed styling
 */

const STYLE_OPTIONS = {
  defaultBackground: 'section',
  defaultHeadingSize: 'medium',
  defaultAlignment: 'center',
  defaultButtonStyle: 'primary',
  defaultPadding: 'normal',
};

const EmailSubscribeRenderer = ({ block, profileId, theme = {} }) => {
  const { content } = block;
  const {
    heading = 'Stay Updated',
    description = '',
    buttonText = 'Subscribe',
    showNameField = true,
  } = content;

  const [formData, setFormData] = useState({ name: '', email: '' });
  const [status, setStatus] = useState('idle'); // idle, loading, success, error, rate-limited
  const [errorMessage, setErrorMessage] = useState('');

  const styles = useBlockStyles(content, theme, STYLE_OPTIONS);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (status === 'error' || status === 'rate-limited') {
      setStatus('idle');
      setErrorMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate email
    if (!formData.email?.trim()) {
      setStatus('error');
      setErrorMessage('Please enter your email address.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      setStatus('error');
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    // Rate limiting - 3 submissions per hour per profile (matches BlogSubscribeButton pattern)
    const rateLimitKey = `subscribe_attempts_${profileId}`;
    const now = Date.now();
    const oneHour = 60 * 60 * 1000;

    try {
      const attemptsData = localStorage.getItem(rateLimitKey);
      const attempts = attemptsData ? JSON.parse(attemptsData) : [];
      const recentAttempts = attempts.filter((timestamp) => now - timestamp < oneHour);

      if (recentAttempts.length >= 3) {
        const oldestAttempt = Math.min(...recentAttempts);
        const timeUntilReset = Math.ceil((oneHour - (now - oldestAttempt)) / 60000);
        setStatus('rate-limited');
        setErrorMessage(
          `Rate limit exceeded. Please try again in ${timeUntilReset} minute${timeUntilReset !== 1 ? 's' : ''}.`
        );
        return;
      }
    } catch (err) {
      console.error('Error checking rate limit:', err);
    }

    setStatus('loading');

    try {
      await addEmailSubscriber(
        profileId,
        formData.email.trim(),
        formData.name?.trim() || '',
        'website-form'
      );

      // Record successful submission for rate limiting
      try {
        const attemptsData = localStorage.getItem(rateLimitKey);
        const attempts = attemptsData ? JSON.parse(attemptsData) : [];
        const recentAttempts = attempts.filter((timestamp) => now - timestamp < oneHour);
        recentAttempts.push(now);
        localStorage.setItem(rateLimitKey, JSON.stringify(recentAttempts));
      } catch (err) {
        console.error('Error updating rate limit:', err);
      }

      setStatus('success');
      setFormData({ name: '', email: '' });
    } catch (error) {
      console.error('Error subscribing:', error);
      setStatus('error');
      setErrorMessage('Failed to subscribe. Please try again.');
    }
  };

  // Success state
  if (status === 'success') {
    return (
      <div
        className={`w-full ${styles.sectionPaddingClass}`}
        style={{ backgroundColor: styles.sectionBackground }}
      >
        <div className="max-w-lg mx-auto text-center">
          <div
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{ backgroundColor: `${styles.accentColor}20` }}
          >
            <CheckCircle size={32} style={{ color: styles.accentColor }} />
          </div>
          <h3
            className="text-xl font-semibold mb-2"
            style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
          >
            Successfully Subscribed!
          </h3>
          <p style={{ color: styles.mutedColor }} className="mb-6">
            You'll receive updates directly to your inbox.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`w-full ${styles.sectionPaddingClass}`}
      style={{ backgroundColor: styles.sectionBackground }}
    >
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          {heading && (
            <h2
              className="text-2xl sm:text-3xl font-bold mb-3"
              style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
            >
              {heading}
            </h2>
          )}
          {description && (
            <p className="whitespace-pre-wrap" style={{ color: styles.mutedColor }}>
              {description}
            </p>
          )}
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="rounded-xl p-6 sm:p-8"
          style={{ backgroundColor: styles.cardBackground }}
        >
          {/* Error/Rate Limit Message */}
          {(status === 'error' || status === 'rate-limited') && (
            <div
              className="mb-6 p-4 rounded-lg flex items-start gap-3"
              style={{
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
              }}
            >
              <AlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-300">{errorMessage}</p>
            </div>
          )}

          {/* Name Field */}
          {showNameField && (
            <div className="mb-4">
              <label
                className="block text-sm font-medium mb-2"
                style={{ color: styles.bodyColor }}
              >
                Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Your name"
                className="w-full px-4 py-3 rounded-lg border transition-colors focus:outline-none"
                style={{
                  backgroundColor: styles.inputBackground,
                  borderColor: styles.borderColor,
                  color: styles.headingColor,
                }}
                onFocus={(e) => (e.target.style.borderColor = styles.accentColor)}
                onBlur={(e) => (e.target.style.borderColor = styles.borderColor)}
              />
            </div>
          )}

          {/* Email Field */}
          <div className="mb-6">
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: styles.bodyColor }}
            >
              Email <span className="text-red-400">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="your@email.com"
              required
              className="w-full px-4 py-3 rounded-lg border transition-colors focus:outline-none"
              style={{
                backgroundColor: styles.inputBackground,
                borderColor: styles.borderColor,
                color: styles.headingColor,
              }}
              onFocus={(e) => (e.target.style.borderColor = styles.accentColor)}
              onBlur={(e) => (e.target.style.borderColor = styles.borderColor)}
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={status === 'loading'}
            className="w-full py-3 px-6 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-60"
            style={styles.getButtonStyles()}
          >
            {status === 'loading' ? (
              <>
                <Loader size={18} className="animate-spin" />
                Subscribing...
              </>
            ) : (
              <>
                <Mail size={18} />
                {buttonText}
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EmailSubscribeRenderer;
